import { DatePickerTR } from "@/components/DatePickerTR";
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtTL } from "@/components/StatusBadge";
import DiscountUsageCard from "@/components/DiscountUsageCard";
import { toast } from "sonner";
import {
  BarChart3, CalendarDays, TrendingUp, TrendingDown, Store, Truck, Trophy,
  Trash2, AlertTriangle, Loader2, Plus, Search, Wallet, PackageX, Tag, FileText, FileSpreadsheet, Mail, X, Undo2,
} from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as ReTooltip } from "recharts";

const BRAND_COLORS = ["#16a34a", "#f97316", "#0284c7", "#7c3aed", "#f59e0b", "#ef4444", "#14b8a6", "#64748b"];

const Trend = ({ v }) => v == null
  ? <span className="font-semibold text-sky-600">Yeni</span>
  : v > 0 ? <span className="font-semibold text-emerald-600">▲ %{v}</span>
    : v < 0 ? <span className="font-semibold text-red-600">▼ %{Math.abs(v)}</span>
      : <span className="text-slate-400">%0</span>;

const PRESETS = [
  { id: "today", label: "Bugün" },
  { id: "yesterday", label: "Dün" },
  { id: "week", label: "Bu Hafta" },
  { id: "month", label: "Bu Ay" },
  { id: "lastMonth", label: "Geçen Ay" },
  { id: "custom", label: "Özel" },
];
const sod = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const eod = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
function rangeFor(preset) {
  const now = new Date();
  if (preset === "today") return { start: sod(now), end: eod(now) };
  if (preset === "yesterday") { const y = new Date(now); y.setDate(now.getDate() - 1); return { start: sod(y), end: eod(y) }; }
  if (preset === "week") { const d = new Date(now); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return { start: sod(d), end: eod(now) }; }
  if (preset === "month") return { start: sod(new Date(now.getFullYear(), now.getMonth(), 1)), end: eod(now) };
  if (preset === "lastMonth") return { start: sod(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: eod(new Date(now.getFullYear(), now.getMonth(), 0)) };
  return null;
}

function RefundAnalysis() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/refunds?days=${days}`).then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [days]);
  const maxReasonAmt = Math.max(1, ...((d?.by_reason || []).map((x) => x.amount)));
  return (
    <div className="rounded-md bg-white ring-1 ring-inset ring-slate-200" data-testid="refund-analysis-card">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
        <Undo2 className="h-4 w-4 text-orange-500" />
        <p className="font-heading font-bold text-slate-800">İade Analizi</p>
        <span className="text-[11px] text-slate-400">En çok iade edilen ürün ve sebepler — fire ve yanlış satışları erken yakalayın.</span>
        <div className="ml-auto flex items-center gap-1">
          {[7, 30, 90].map((n) => (
            <button key={n} data-testid={`refund-days-${n}`} onClick={() => setDays(n)}
              className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${days === n ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{n} gün</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center p-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : !d ? (
        <div className="p-6 text-center text-sm text-slate-400">Veri yüklenemedi</div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-md bg-orange-50 p-3 ring-1 ring-inset ring-orange-100"><p className="text-[11px] font-medium text-slate-500">Toplam İade</p><p data-testid="refund-total-amount" className="tabular font-heading text-xl font-bold text-orange-600">{fmtTL(d.total_amount)}</p></div>
            <div className="rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200"><p className="text-[11px] font-medium text-slate-500">İade Adedi</p><p data-testid="refund-total-count" className="tabular font-heading text-xl font-bold text-slate-700">{d.total_count}</p></div>
            <div className="rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200"><p className="text-[11px] font-medium text-slate-500">İade Oranı</p><p data-testid="refund-rate" className="tabular font-heading text-xl font-bold text-slate-700">%{d.refund_rate_pct}</p></div>
            <div className="rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200"><p className="text-[11px] font-medium text-slate-500">Dönem Cirosu</p><p className="tabular font-heading text-xl font-bold text-emerald-600">{fmtTL(d.gross_amount)}</p></div>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-[12px] font-semibold text-slate-600">En Çok İade Edilen Ürünler</p>
              {(d.top_products || []).length === 0 ? <p className="text-xs text-slate-400">Dönemde iade yok.</p> : (
                <div className="divide-y divide-slate-100 rounded-md ring-1 ring-inset ring-slate-200">
                  {d.top_products.map((p, i) => (
                    <div key={i} data-testid={`refund-product-${i}`} className="flex items-center gap-3 px-3 py-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-[11px] font-bold text-orange-700">{i + 1}</span>
                      <span className="min-w-0 flex-1 truncate text-[13px] text-slate-700">{p.name}</span>
                      <span className="text-[11px] text-slate-400">{p.qty} adet</span>
                      <span className="tabular text-[13px] font-semibold text-orange-600">{fmtTL(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="mb-2 text-[12px] font-semibold text-slate-600">İade Sebepleri</p>
              {(d.by_reason || []).length === 0 ? <p className="text-xs text-slate-400">Dönemde iade yok.</p> : (
                <div className="space-y-2">
                  {d.by_reason.map((r, i) => (
                    <div key={i} data-testid={`refund-reason-${i}`}>
                      <div className="flex items-center justify-between text-[12px]"><span className="text-slate-600">{r.reason}</span><span className="tabular font-semibold text-slate-700">{fmtTL(r.amount)} · {r.count}x</span></div>
                      <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-orange-400" style={{ width: `${Math.round((r.amount / maxReasonAmt) * 100)}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const { can } = useAuth();
  const canWaste = can("stock.adjust");
  const [preset, setPreset] = useState("month");
  const [cStart, setCStart] = useState("");
  const [cEnd, setCEnd] = useState("");
  const [data, setData] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [channel, setChannel] = useState("");
  const [hideUnbranded, setHideUnbranded] = useState(false);
  const [emailDlg, setEmailDlg] = useState({ open: false, to: "", sending: false });
  const [savedEmails, setSavedEmails] = useState([]);
  const [loading, setLoading] = useState(false);

  const { start, end } = useMemo(() => {
    if (preset === "custom") return (cStart && cEnd) ? { start: sod(cStart), end: eod(cEnd) } : { start: null, end: null };
    return rangeFor(preset);
  }, [preset, cStart, cEnd]);

  const load = useCallback(() => {
    if (!start || !end) return;
    setLoading(true);
    const qs = `start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`;
    Promise.all([
      api.get(`/reports/summary?${qs}`),
      api.get(`/reports/brand-summary?${qs}${channel ? `&channel=${channel}` : ""}`),
    ]).then(([a, b]) => { setData(a.data); setBrandData(b.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [start, end, channel]);

  const exportBrandPdf = async () => {
    if (!start || !end) return;
    try {
      const qs = `start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}${channel ? `&channel=${channel}` : ""}`;
      const res = await api.get(`/reports/brand-summary/pdf?${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { toast.error("PDF oluşturulamadı"); }
  };
  const exportBrandXlsx = async () => {
    if (!start || !end) return;
    try {
      const qs = `start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}${channel ? `&channel=${channel}` : ""}`;
      const res = await api.get(`/reports/brand-summary/xlsx?${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `marka-raporu${channel ? "-" + channel : ""}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Excel oluşturulamadı"); }
  };
  const openEmailDlg = async () => {
    setEmailDlg({ open: true, to: "", sending: false });
    try { const { data } = await api.get("/settings/report-recipients"); setSavedEmails(data.emails || []); } catch { /* yoksay */ }
  };
  const sendBrandEmail = async () => {
    const to = (emailDlg.to || "").trim();
    if (!to || !to.includes("@")) { toast.error("Geçerli bir e-posta girin"); return; }
    setEmailDlg((s) => ({ ...s, sending: true }));
    try {
      await api.post("/reports/brand-summary/email", {
        to, start: start.toISOString(), end: end.toISOString(), channel: channel || null,
      });
      toast.success("Rapor e-posta ile gönderildi: " + to);
      setEmailDlg({ open: false, to: "", sending: false });
    } catch (e) {
      toast.error(apiError(e) || "E-posta gönderilemedi");
      setEmailDlg((s) => ({ ...s, sending: false }));
    }
  };
  const removeSavedEmail = async (email) => {
    const next = savedEmails.filter((x) => x !== email);
    setSavedEmails(next);
    if (emailDlg.to === email) setEmailDlg((s) => ({ ...s, to: "" }));
    try { await api.put("/settings/report-recipients", { emails: next }); toast.success("Alıcı silindi"); }
    catch { toast.error("Alıcı silinemedi"); setSavedEmails(savedEmails); }
  };
  useEffect(() => { load(); }, [load]);

  const canCost = data?.can_cost;
  const t = data?.totals || {};
  const allBrandRows = brandData?.brands || [];
  const brandRows = hideUnbranded ? allBrandRows.filter((b) => b.brand !== "Markasız" && b.brand !== "Belirsiz") : allBrandRows;
  const brandChart = brandRows.slice(0, 8);
  const selBrand = brandRows.find((b) => b.brand === selectedBrand);

  return (
    <div className="space-y-4 fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Analiz</p><h1 className="font-heading text-2xl font-bold text-slate-900">Raporlar</h1><p className="mt-1 text-sm text-slate-500" data-testid="reports-purpose">Dönemsel satış analizi — kanal kârlılığı, en çok satan, fire ve SKT riski. Mali/vergi kaydı için <b>Muhasebe</b>, canlı gün için <b>Panel</b>.</p></div>
        </div>
      </div>

      {can("role.manage") && <DiscountUsageCard />}

      {/* Date presets */}
      <div className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarDays className="h-4 w-4 text-slate-400" />
          {PRESETS.map((p) => (
            <button key={p.id} data-testid={`rep-preset-${p.id}`} onClick={() => setPreset(p.id)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${preset === p.id ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {p.label}
            </button>
          ))}
          {loading && <Loader2 className="ml-2 h-4 w-4 animate-spin text-slate-400" />}
        </div>
        {preset === "custom" && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <DatePickerTR testid="rep-custom-start" value={cStart} onChange={(v) => setCStart(v)} className="mt-0 h-9 w-40" />
            <DatePickerTR testid="rep-custom-end" value={cEnd} onChange={(v) => setCEnd(v)} className="mt-0 h-9 w-40" />
          </div>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-md bg-brand-green p-4 text-white">
          <p className="text-xs text-emerald-100">Ciro (net)</p>
          <p data-testid="rep-revenue" className="tabular font-heading text-2xl font-bold">{fmtTL(t.revenue ?? 0)}</p>
          <p className="text-[11px] text-emerald-200">{t.sale_count ?? 0} satış</p>
        </div>
        {canCost ? (
          <div className="rounded-md bg-slate-900 p-4 text-white">
            <p className="text-xs text-slate-300">Brüt Kâr</p>
            <p data-testid="rep-profit" className="tabular font-heading text-2xl font-bold text-emerald-400">{fmtTL(t.profit ?? 0)}</p>
            <p className="text-[11px] text-slate-400">marj %{t.margin_pct ?? 0}</p>
          </div>
        ) : (
          <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
            <p className="overline">Satış Adedi</p>
            <p className="tabular font-heading text-2xl font-bold text-slate-900">{t.qty ?? 0}</p>
          </div>
        )}
        {canCost && (
          <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
            <p className="overline">Maliyet</p>
            <p className="tabular font-heading text-2xl font-bold text-slate-700">{fmtTL(t.cost ?? 0)}</p>
          </div>
        )}
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="overline flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Fire / Zayiat</p>
          <p data-testid="rep-waste" className="tabular font-heading text-2xl font-bold text-red-600">
            {canCost ? fmtTL(data?.waste?.total_cost ?? 0) : (data?.waste?.total_qty ?? 0)}
          </p>
          <p className="text-[11px] text-slate-400">{data?.waste?.total_qty ?? 0} adet/kg</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Channel profitability */}
        <div className="rounded-md bg-white ring-1 ring-inset ring-slate-200">
          <div className="border-b border-slate-100 p-4"><p className="font-heading font-bold text-slate-800">Kanal Kârlılığı</p></div>
          <div className="divide-y divide-slate-100">
            {(data?.channels || []).length === 0 && <p className="p-6 text-center text-sm text-slate-400">Bu dönemde satış yok.</p>}
            {(data?.channels || []).map((c) => (
              <div key={c.channel} data-testid={`rep-channel-${c.channel}`} className="flex items-center gap-3 p-4">
                <div className={`flex h-10 w-10 items-center justify-center rounded-md ${c.channel === "wholesale" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {c.channel === "wholesale" ? <Truck className="h-5 w-5" /> : <Store className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800">{c.label}</p>
                  <p className="text-[11px] text-slate-400">{c.count} satış · {c.qty} adet</p>
                </div>
                <div className="text-right">
                  <p className="tabular text-sm font-bold text-slate-900">{fmtTL(c.revenue)}</p>
                  {canCost && (
                    <p className={`tabular text-[11px] font-semibold ${c.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      kâr {fmtTL(c.profit)} · %{c.margin_pct}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {(data?.payment_mix || []).length > 0 && (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 p-3">
              {data.payment_mix.map((p) => (
                <span key={p.method} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                  <Wallet className="h-3 w-3" /> {p.method === "cash" ? "Nakit" : "Kart"}: {p.count} · {fmtTL(p.total)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Top products */}
        <div className="rounded-md bg-white ring-1 ring-inset ring-slate-200">
          <div className="flex items-center gap-2 border-b border-slate-100 p-4">
            <Trophy className="h-4 w-4 text-brand-orange" /><p className="font-heading font-bold text-slate-800">En Çok Satan Ürünler</p>
          </div>
          <div className="divide-y divide-slate-100">
            {(data?.top_products || []).length === 0 && <p className="p-6 text-center text-sm text-slate-400">Veri yok.</p>}
            {(data?.top_products || []).map((p, i) => (
              <div key={p.product_id} data-testid={`rep-top-${i}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${i < 3 ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{p.name}</p>
                  <p className="font-mono text-[10px] text-slate-400">{p.sku}</p>
                </div>
                <div className="text-right">
                  <p className="tabular text-sm font-semibold text-slate-900">{p.qty} adet</p>
                  <p className="tabular text-[11px] text-slate-400">{fmtTL(p.revenue)}{canCost && p.profit != null ? ` · kâr ${fmtTL(p.profit)}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <RefundAnalysis />

      {/* Markaya göre ciro / kâr */}
      <div className="rounded-md bg-white ring-1 ring-inset ring-slate-200" data-testid="rep-brand-card">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
          <Tag className="h-4 w-4 text-brand-green" />
          <p className="font-heading font-bold text-slate-800">Markaya Göre Ciro / Kâr</p>
          <span className="text-[11px] text-slate-400">Marka seçin (pastadaki dilime veya satıra tıklayın) — ciro payını grafikte görün.</span>
          <select data-testid="rep-brand-channel" value={channel} onChange={(e) => setChannel(e.target.value)}
            className="ml-auto h-8 rounded-md border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-green">
            <option value="">Tüm Kanallar</option>
            <option value="pos">Perakende (Market)</option>
            <option value="wholesale">Toptan</option>
          </select>
          <label data-testid="rep-brand-hide-unbranded" className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-slate-600">
            <input type="checkbox" checked={hideUnbranded} onChange={(e) => setHideUnbranded(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-brand-green focus:ring-brand-green" />
            Markasız gizle
          </label>
          <button data-testid="rep-brand-xlsx" onClick={exportBrandXlsx}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-800">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel İndir
          </button>
          <button data-testid="rep-brand-pdf" onClick={exportBrandPdf}
            className="inline-flex items-center gap-1 rounded-md bg-brand-green px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600">
            <FileText className="h-3.5 w-3.5" /> PDF İndir
          </button>
          <button data-testid="rep-brand-email" onClick={openEmailDlg}
            className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700">
            <Mail className="h-3.5 w-3.5" /> E-posta
          </button>
          <Dialog open={emailDlg.open} onOpenChange={(o) => setEmailDlg((s) => ({ ...s, open: o }))}>
            <DialogContent className="max-w-md" data-testid="rep-brand-email-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4 text-brand-green" /> Marka Raporunu E-posta Gönder</DialogTitle>
                <DialogDescription>Seçili dönem ve kanal ({channel === "pos" ? "Perakende" : channel === "wholesale" ? "Toptan" : "Tüm Kanallar"}) için marka ciro/kâr raporu, girilen adrese e-posta ile gönderilir. Adres kayıtlı listeye eklenir.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {savedEmails.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500">Kayıtlı alıcılar (tıklayıp seçin)</Label>
                    <div className="mt-1 flex flex-wrap gap-1.5" data-testid="rep-brand-email-saved">
                      {savedEmails.map((e) => (
                        <span key={e} className={`group inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${emailDlg.to === e ? "bg-brand-green text-white ring-brand-green" : "bg-slate-50 text-slate-600 ring-slate-200 hover:bg-slate-100"}`}>
                          <button type="button" data-testid={`rep-brand-email-chip-${e}`} onClick={() => setEmailDlg((s) => ({ ...s, to: e }))}>{e}</button>
                          <button type="button" data-testid={`rep-brand-email-chip-del-${e}`} title="Alıcıyı sil" onClick={() => removeSavedEmail(e)}
                            className={`rounded-full p-0.5 transition-colors ${emailDlg.to === e ? "hover:bg-white/25" : "hover:bg-slate-200 text-slate-400 hover:text-red-600"}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">E-posta adresi</Label>
                  <Input data-testid="rep-brand-email-input" type="email" value={emailDlg.to} placeholder="ornek@malimusavir.com"
                    onChange={(e) => setEmailDlg((s) => ({ ...s, to: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && sendBrandEmail()} className="mt-1" />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => setEmailDlg((s) => ({ ...s, open: false }))}>İptal</Button>
                  <Button data-testid="rep-brand-email-send" size="sm" disabled={emailDlg.sending} onClick={sendBrandEmail}
                    className="bg-brand-green text-white hover:bg-emerald-600">
                    {emailDlg.sending ? "Gönderiliyor…" : "Gönder"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {brandRows.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-400" data-testid="rep-brand-empty">Bu dönemde marka bazlı satış verisi yok.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
            <div data-testid="rep-brand-chart" className="flex flex-col items-center justify-center">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={brandChart} dataKey="revenue" nameKey="brand" cx="50%" cy="50%" innerRadius={62} outerRadius={96} paddingAngle={2}
                    onClick={(d) => setSelectedBrand(d && d.brand === selectedBrand ? "" : (d?.brand || ""))}>
                    {brandChart.map((b, i) => (
                      <Cell key={b.brand} fill={BRAND_COLORS[i % BRAND_COLORS.length]} cursor="pointer"
                        opacity={selectedBrand && selectedBrand !== b.brand ? 0.3 : 1}
                        stroke={selectedBrand === b.brand ? "#0f172a" : "#fff"} strokeWidth={selectedBrand === b.brand ? 3 : 1} />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(v, n, p) => [fmtTL(v), p?.payload?.brand]} />
                </PieChart>
              </ResponsiveContainer>
              {selBrand ? (
                <div data-testid="rep-brand-selected" className="mt-1 text-center">
                  <p className="text-sm font-bold text-slate-800">{selBrand.brand}</p>
                  <p className="text-xs text-slate-500">%{selBrand.share_pct} ciro payı · {fmtTL(selBrand.revenue)}{canCost ? ` · kâr ${fmtTL(selBrand.profit)} (%${selBrand.margin_pct})` : ""}</p>
                </div>
              ) : (
                <p className="mt-1 text-center text-[11px] text-slate-400">{brandRows.length} marka · toplam ciro {fmtTL(brandData?.total_revenue || 0)}</p>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-400">
                  <tr className="border-b border-slate-100">
                    <th className="px-2 py-2 font-medium">Marka</th>
                    <th className="px-2 py-2 text-right font-medium">Adet</th>
                    <th className="px-2 py-2 text-right font-medium">Ciro</th>
                    <th className="px-2 py-2 text-right font-medium">Pay</th>
                    <th className="px-2 py-2 text-right font-medium">Trend</th>
                    {canCost && <th className="px-2 py-2 text-right font-medium">Kâr</th>}
                    {canCost && <th className="px-2 py-2 text-right font-medium">Marj</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {brandRows.map((b, i) => (
                    <tr key={b.brand} data-testid={`rep-brand-row-${i}`} onClick={() => setSelectedBrand(b.brand === selectedBrand ? "" : b.brand)}
                      className={`cursor-pointer transition-colors ${selectedBrand === b.brand ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
                      <td className="px-2 py-2 font-medium text-slate-800">
                        <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: BRAND_COLORS[i % BRAND_COLORS.length] }} />
                        {b.brand}
                      </td>
                      <td className="px-2 py-2 text-right tabular text-slate-600">{b.qty}</td>
                      <td className="px-2 py-2 text-right tabular font-semibold text-slate-900">{fmtTL(b.revenue)}</td>
                      <td className="px-2 py-2 text-right tabular text-slate-500">%{b.share_pct}</td>
                      <td className="px-2 py-2 text-right text-[11px]"><Trend v={b.trend_pct} /></td>
                      {canCost && <td className={`px-2 py-2 text-right tabular font-semibold ${b.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtTL(b.profit)}</td>}
                      {canCost && <td className="px-2 py-2 text-right tabular text-slate-500">%{b.margin_pct}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Waste breakdown + expiry risk */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-md bg-white ring-1 ring-inset ring-slate-200">
          <div className="flex items-center gap-2 border-b border-slate-100 p-4">
            <Trash2 className="h-4 w-4 text-red-500" /><p className="font-heading font-bold text-slate-800">Fire / Zayiat Dökümü</p>
          </div>
          <div className="p-4">
            {(data?.waste?.by_reason || []).length === 0 ? (
              <p className="text-sm text-slate-400">Bu dönemde fire kaydı yok.</p>
            ) : (
              <div className="space-y-2">
                {data.waste.by_reason.map((b, i) => (
                  <div key={i} data-testid={`rep-waste-reason-${i}`} className="flex items-center justify-between rounded-sm bg-red-50 px-3 py-2">
                    <span className="text-sm text-slate-700">{b.reason}</span>
                    <span className="tabular text-sm font-semibold text-red-700">{b.qty} adet{canCost && b.cost != null ? ` · ${fmtTL(b.cost)}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-md bg-white ring-1 ring-inset ring-slate-200">
          <div className="flex items-center gap-2 border-b border-slate-100 p-4">
            <AlertTriangle className="h-4 w-4 text-orange-500" /><p className="font-heading font-bold text-slate-800">SKT Geçen / Yaklaşan (Fire Riski)</p>
          </div>
          <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
            {(data?.expiring_risk || []).length === 0 && <p className="p-6 text-center text-sm text-slate-400">Riskli lot yok.</p>}
            {(data?.expiring_risk || []).map((r, i) => (
              <div key={i} data-testid={`rep-risk-${i}`} className="flex items-center justify-between px-4 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{r.name}</p>
                  <p className="text-[11px] text-slate-400">{r.remaining} adet · SKT {new Date(r.expiry_date).toLocaleDateString("tr-TR")}</p>
                </div>
                <span className={`shrink-0 rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${r.expired ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}`}>
                  {r.expired ? "SKT GEÇTİ" : "YAKLAŞIYOR"}{canCost && r.cost_value != null ? ` · ${fmtTL(r.cost_value)}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <WasteDialogRemoved />
    </div>
  );
}

function WasteDialogRemoved() { return null; }
